import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { EnvironmentThemeProvider } from '@codex-brightworld/themes';
import { Close } from '@codex-brightworld/organisms';

import ProductInfo from '../../../ProductInfo/ProductInfo';
import ActionButtons from '../../../ActionButtons/ActionButtons';
import WeHelpHeading from '../WeHelpHeading/WeHelpHeading';
import Container from '../Container/Container';

const StyledContainer = styled.div`
  position: relative;
  width: 440px;
  max-width: 440px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f2f2f2;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    width: 100%;
  }
`;

const StyledClose = styled(Close)`
  position: absolute;
  top: 15px;
  right: 27px;
  z-index: 1;
  width: 10px;
  height: 10px;
  stroke: none;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    right: 19px;
  }
  
  > span {
    > svg {
      > g {
        /* Hide border of the icon */
        path:nth-child(1) {
          display: none
        }
      }
    }
  }
`;

const TopBlockWrapper = styled.div`
  padding: 56px 19px 2px 19px;
  background: #fff;
  position: relative;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    padding: 53px 10px 2px 10px;
  }
`;

const ProductInfoWrapper = styled.div`
  padding-bottom: 16px;
`;

const ScrollWrapper = styled.div`
  position: relative;
  height: 100%;
  overflow-y: scroll;
`;

const Wrapper = ({
  productName,
  productManufacturer,
  productImgUrl,
  productPrice,
  isProductBundle,
  continueToBasketButtonLabel,
  continueShoppingButtonLabel,
  continueToBasketButtonAction,
  continueShoppingButtonAction,
  headerItemLabel,
  headerBundleLabel,
  weHelpHeadingLabel,
  installation,
  recycling,
  installationInProgress,
  recyclingInProgress,
  recyclingFailed,
  installationFailed,
  failedToUpdateBasket,
  pleaseTryAgain
}) => {
  //  reference array to all attachments
  const parentContainerRef = useRef(null);

  let productTitle = productName;
  if (productName.indexOf(productManufacturer) === -1) {
    productTitle = `${productManufacturer} ${productName}`;
  }

  return (
    <EnvironmentThemeProvider>
      <StyledContainer ref={parentContainerRef}>
        <StyledClose onClose={continueShoppingButtonAction} />
        <TopBlockWrapper>
          <ProductInfoWrapper>
            <ProductInfo
              productImgUrl={productImgUrl}
              productTitle={productTitle}
              productPrice={productPrice}
              translation={isProductBundle ? headerBundleLabel : headerItemLabel}
            />
          </ProductInfoWrapper>
          <ActionButtons
            primaryButtonLabel={continueToBasketButtonLabel}
            primaryButtonOnClick={continueToBasketButtonAction}
            secondaryButtonLabel={continueShoppingButtonLabel}
            secondaryButtonOnClick={continueShoppingButtonAction}
          />
        </TopBlockWrapper>
        <ScrollWrapper>
          <WeHelpHeading
            text={weHelpHeadingLabel}
          />
          <Container
            installation={installation}
            recycling={recycling}
            installationInProgress={installationInProgress}
            recyclingInProgress={recyclingInProgress}
            recyclingFailed={recyclingFailed}
            installationFailed={installationFailed}
            failedToUpdateBasket={failedToUpdateBasket}
            pleaseTryAgain={pleaseTryAgain}
          />
        </ScrollWrapper>
      </StyledContainer>
    </EnvironmentThemeProvider>
  );
};

Wrapper.propTypes = {
  productName: PropTypes.string.isRequired,
  productManufacturer: PropTypes.string.isRequired,
  productImgUrl: PropTypes.string.isRequired,
  productPrice: PropTypes.number.isRequired,
  isProductBundle: PropTypes.bool.isRequired,
  continueToBasketButtonLabel: PropTypes.string.isRequired,
  continueShoppingButtonLabel: PropTypes.string.isRequired,
  continueToBasketButtonAction: PropTypes.func.isRequired,
  continueShoppingButtonAction: PropTypes.func.isRequired,
  headerItemLabel: PropTypes.string,
  headerBundleLabel: PropTypes.string,
  weHelpHeadingLabel: PropTypes.string.isRequired,
  installation: PropTypes.shape({
    display: PropTypes.bool.isRequired,
    productId: PropTypes.number.isRequired,
    typeText: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    productPrice: PropTypes.number.isRequired,
    productCurrency: PropTypes.string,
    addOnClick: PropTypes.func.isRequired,
    moreDetails: PropTypes.shape({
      isExpanded: PropTypes.bool.isRequired,
      html: PropTypes.string.isRequired
    }).isRequired
  }),
  recycling: PropTypes.shape({
    display: PropTypes.bool.isRequired,
    productId: PropTypes.number.isRequired,
    typeText: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    productPrice: PropTypes.number.isRequired,
    productCurrency: PropTypes.string,
    addOnClick: PropTypes.func.isRequired,
    moreDetails: PropTypes.shape({
      isExpanded: PropTypes.bool.isRequired,
      html: PropTypes.string.isRequired
    }).isRequired
  }),
  installationInProgress: PropTypes.bool,
  recyclingInProgress: PropTypes.bool,
  recyclingFailed: PropTypes.bool,
  installationFailed: PropTypes.bool,
  failedToUpdateBasket: PropTypes.string.isRequired,
  pleaseTryAgain: PropTypes.string.isRequired
};

Wrapper.defaultProps = {
  headerItemLabel: '',
  headerBundleLabel: '',
  installation: null,
  recycling: null,
  installationInProgress: false,
  recyclingInProgress: false,
  installationFailed: false,
  recyclingFailed: false
};

export default Wrapper;
